import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, Save, BellRing, FolderPlus, X } from "lucide-react";

const DEFAULT_REMINDER = {
    days_before: [7, 3, 1],
    remind_on_due_date: true,
    overdue_reminders: true,
    email_enabled: true,
    inapp_enabled: true,
};

export default function Settings() {
    const { user } = useAuth();
    const [firm, setFirm] = useState({});
    const [pw, setPw] = useState({ current_password: "", new_password: "" });
    const [emailStatus, setEmailStatus] = useState(null);
    const [reminder, setReminder] = useState(DEFAULT_REMINDER);
    const [daysBeforeInput, setDaysBeforeInput] = useState("7, 3, 1");
    const [docCats, setDocCats] = useState({ builtin: [], custom: [] });
    const [newCat, setNewCat] = useState("");

    useEffect(() => {
        api.get("/settings/firm").then((r) => {
            setFirm(r.data);
            const rc = r.data?.reminder_config;
            if (rc) {
                setReminder({ ...DEFAULT_REMINDER, ...rc });
                if (Array.isArray(rc.days_before)) {
                    setDaysBeforeInput(rc.days_before.join(", "));
                }
            }
        });
        api.get("/email/status").then((r) => setEmailStatus(r.data)).catch(() => {});
        api.get("/documents/categories").then((r) => setDocCats(r.data)).catch(() => {});
    }, []);

    const addCat = async () => {
        const name = newCat.trim();
        if (!name) return;
        try {
            await api.post("/documents/categories", { name });
            setNewCat("");
            const r = await api.get("/documents/categories");
            setDocCats(r.data);
            toast.success("Category added");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const removeCat = async (name) => {
        await api.delete(`/documents/categories/${encodeURIComponent(name)}`);
        const r = await api.get("/documents/categories");
        setDocCats(r.data);
    };

    const saveReminder = async () => {
        const parsed = daysBeforeInput
            .split(",")
            .map((v) => parseInt(v.trim(), 10))
            .filter((v) => Number.isInteger(v) && v > 0 && v <= 60);
        if (parsed.length === 0) {
            toast.error("Enter at least one valid day (1-60).");
            return;
        }
        const rc = { ...reminder, days_before: parsed };
        try {
            await api.patch("/settings/firm", { reminder_config: rc });
            setReminder(rc);
            toast.success("Reminder settings saved");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const saveFirm = async () => {
        try {
            await api.patch("/settings/firm", {
                firm_name: firm.name,
                address: firm.address,
                phone: firm.phone,
                email: firm.email,
                gstin: firm.gstin,
                website: firm.website,
                logo_url: firm.logo_url,
            });
            toast.success("Firm settings updated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const savePw = async () => {
        try {
            await api.post("/auth/change-password", pw);
            toast.success("Password updated");
            setPw({ current_password: "", new_password: "" });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="settings-page">
            <PageHeader title="Settings" subtitle="Firm profile, security and preferences" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-6 lg:col-span-2">
                    <h3 className="font-display text-lg font-semibold">Firm profile</h3>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                        <F label="Firm name">
                            <Input value={firm.name || ""} onChange={(e) => setFirm({ ...firm, name: e.target.value })} />
                        </F>
                        <F label="Email">
                            <Input type="email" value={firm.email || ""} onChange={(e) => setFirm({ ...firm, email: e.target.value })} />
                        </F>
                        <F label="Phone">
                            <Input value={firm.phone || ""} onChange={(e) => setFirm({ ...firm, phone: e.target.value })} />
                        </F>
                        <F label="GSTIN">
                            <Input value={firm.gstin || ""} onChange={(e) => setFirm({ ...firm, gstin: e.target.value })} />
                        </F>
                        <F label="Website">
                            <Input value={firm.website || ""} onChange={(e) => setFirm({ ...firm, website: e.target.value })} />
                        </F>
                        <F label="Plan">
                            <Input value={firm.plan || "trial"} disabled />
                        </F>
                        <div className="col-span-2">
                            <F label="Address">
                                <Textarea rows={2} value={firm.address || ""} onChange={(e) => setFirm({ ...firm, address: e.target.value })} />
                            </F>
                        </div>
                    </div>
                    <div className="mt-4">
                        <Button onClick={saveFirm} className="bg-primary hover:bg-primary/90" data-testid="save-firm">
                            <Save size={14} className="mr-1.5" /> Save changes
                        </Button>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                        <KeyRound size={16} /> Change password
                    </h3>
                    <div className="mt-4 space-y-3">
                        <F label="Current password">
                            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
                        </F>
                        <F label="New password">
                            <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
                        </F>
                        <Button onClick={savePw} className="bg-primary hover:bg-primary/90 w-full" data-testid="save-password">
                            Update password
                        </Button>
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500">
                        Logged in as <span className="font-medium text-slate-700">{user?.email}</span>
                        <div>Role: {user?.role}</div>
                    </div>
                    {emailStatus && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
                                Email pipeline
                            </div>
                            <div className="text-sm text-slate-700 flex items-center gap-2">
                                <span
                                    className={`inline-flex w-2 h-2 rounded-full ${
                                        emailStatus.smtp_configured ? "bg-emerald-500" : "bg-amber-500"
                                    }`}
                                />
                                {emailStatus.smtp_configured
                                    ? "SMTP configured — reminders send live"
                                    : "SMTP not configured — reminders queued in outbox"}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                Set SMTP_HOST / SMTP_PORT / SMTP_USER / SMTP_PASSWORD / SMTP_FROM in the
                                backend .env to turn on delivery.
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            {/* Reminder configuration */}
            <Card className="p-6" data-testid="reminder-config">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                    <BellRing size={16} /> Reminder configuration
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                    Applied firm-wide by the reminder scheduler.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    <F label="Send reminders how many days before deadline (comma-separated)">
                        <Input
                            value={daysBeforeInput}
                            onChange={(e) => setDaysBeforeInput(e.target.value)}
                            placeholder="7, 3, 1"
                            data-testid="reminder-days"
                        />
                    </F>
                    <div className="space-y-3 pt-6">
                        <Toggle
                            label="Also remind on the due date"
                            value={reminder.remind_on_due_date}
                            onChange={(v) => setReminder({ ...reminder, remind_on_due_date: v })}
                        />
                        <Toggle
                            label="Send overdue reminders"
                            value={reminder.overdue_reminders}
                            onChange={(v) => setReminder({ ...reminder, overdue_reminders: v })}
                        />
                        <Toggle
                            label="Send by email"
                            value={reminder.email_enabled}
                            onChange={(v) => setReminder({ ...reminder, email_enabled: v })}
                        />
                        <Toggle
                            label="Send as in-app notification"
                            value={reminder.inapp_enabled}
                            onChange={(v) => setReminder({ ...reminder, inapp_enabled: v })}
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <Button onClick={saveReminder} className="bg-primary hover:bg-primary/90" data-testid="save-reminder">
                        <Save size={14} className="mr-1.5" /> Save reminder settings
                    </Button>
                </div>
            </Card>

            {/* Document categories */}
            <Card className="p-6" data-testid="doc-categories">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                    <FolderPlus size={16} /> Document categories
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                    Built-in categories are always available. Add firm-specific categories here.
                </p>
                <div className="mt-4">
                    <div className="field-label mb-2">Built-in</div>
                    <div className="flex flex-wrap gap-1.5">
                        {docCats.builtin.map((c) => (
                            <span key={c} className="badge-status bg-slate-50 text-slate-700 border-slate-200">
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="mt-4">
                    <div className="field-label mb-2">Custom</div>
                    <div className="flex flex-wrap gap-1.5">
                        {docCats.custom.length === 0 && (
                            <div className="text-xs text-slate-500">No custom categories yet.</div>
                        )}
                        {docCats.custom.map((c) => (
                            <span key={c} className="inline-flex items-center gap-1 badge-status bg-blue-50 text-primary border-blue-200">
                                {c}
                                <button
                                    onClick={() => removeCat(c)}
                                    className="hover:text-rose-600"
                                    title="Delete"
                                >
                                    <X size={11} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="mt-3 flex gap-2 max-w-md">
                        <Input
                            placeholder="New category name (e.g. Board Resolutions)"
                            value={newCat}
                            onChange={(e) => setNewCat(e.target.value)}
                            data-testid="new-cat-input"
                        />
                        <Button onClick={addCat} variant="outline" data-testid="add-cat-btn">
                            <FolderPlus size={13} className="mr-1.5" /> Add
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}

const Toggle = ({ label, value, onChange }) => (
    <label className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
        <Switch checked={value} onCheckedChange={onChange} />
        {label}
    </label>
);

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
